// /finance's Cash (finance redesign slice 4, J18; cash where-we-stand, DD-041, DD-054, TD-076): where the agency stands, 30
// days ago, today and in 30 days, and one line through every day between. The range picker in the header row changes the
// span, 30 days by default. Where we stand on a day is cash plus what clients owe for work done to that day, sent or not,
// less what the team is owed for it and the costs run up and not paid; sending an invoice or a payment never moves it.
// Ahead, each recurring series' next periods are counted at their price and claims and never stored (TD-076). Without the
// agency's cash there is no level (A4): the strip's ends and the line show the change from today, and a note asks for the
// figure in Setup. The strip ends on the health line (round 4): profit each month, fronted for clients and what is overdue.
// What today is made of comes under it, then its breakdowns by client, person and expense, each row opening its drawer
// (DD-051, DD-045). Every figure is the server's (TD-102). On jobs (finance consolidation S9): what clients owe and what
// the team is owed come from jobs, claims and invoices, a paused job counted on neither side, and the health line's
// margin and own work are the Clients tab's footer over every client. That margin is price less claims, before any
// write-off, so the health line names it apart from the profit it pays for.
import { Link } from "react-router-dom";
import { MoneyText } from "@/components/ui/MoneyText";
import { StatStrip, StatStripPending, type Stat } from "@/components/ui/StatStrip";
import { ENTITY_CONFIG } from "@/core/entityConfig";
import { formatMoney } from "@/utils/currency";
import { TabPending } from "../components/TabPending";
import type { ParamPatch } from "../hooks/useFinanceParams";
import type { AgencyCashView, CashDays, CashHealth, LineDay } from "../types";
import { dateLabel } from "../utils/format";
import { CashChart } from "./CashChart";
import { CASH_RANGES, fromToday, toDays } from "./cashText";
import { PositionPanels, TodayMadeOf } from "./PositionPanels";
import { useCashTab } from "./hooks/useCashTab";

/** Setup's Agency tab with the agency cash drawer open. */
export const SET_AGENCY_CASH = "/setup?modal=agency-cash";

const FRONTED = `Fronted for ${ENTITY_CONFIG.client.plural.toLowerCase()}`;
const EXPENSES = `business ${ENTITY_CONFIG.expense.plural.toLowerCase()}`;

/** The range picker, in the page header's row beside the tabs while Cash is open. */
export function CashRange({ days, onDays }: { days: CashDays; onDays: (days: CashDays) => void }) {
  return (
    <select
      aria-label="Range"
      value={String(days)}
      onChange={(e) => onDays(toDays(e.target.value))}
      className="h-8 w-56 text-sm border border-gray-300 rounded-lg px-3 bg-white"
    >
      {CASH_RANGES.map((n) => (
        <option key={n} value={String(n)}>
          {`${n} days back and ahead`}
        </option>
      ))}
    </select>
  );
}

/** Under the strip while the agency's cash is not set (J18, A4): the line has no level, and the way to make one. */
function BooksNote() {
  return (
    <p className="mt-3 text-xs text-gray-500">
      {"Without the agency's cash the line shows the change from today. "}
      <Link to={SET_AGENCY_CASH} className="text-blue-600 hover:text-blue-700">
        {`Set the agency's cash in ${ENTITY_CONFIG.setup.label}`}
      </Link>
      {" to see where we stand."}
    </p>
  );
}

/**
 * What the margin pays each month, as one list: business expenses, BeNerdy's share of the pots (slice 12 R8) and the
 * agency's own work, each after the first left out at nothing, with "and" before the last.
 */
function lessText(health: CashHealth, money: (minor: number) => string): string {
  const parts = [
    `${money(health.expensesMonthlyMinor)} ${EXPENSES}`,
    ...(health.potsMonthlyMinor > 0 ? [`${money(health.potsMonthlyMinor)} of the pots`] : []),
    ...(health.ownWorkMonthlyMinor > 0 ? [`${money(health.ownWorkMonthlyMinor)} of the agency's own work`] : []),
  ];
  return parts.length === 1 ? parts[0] : `${parts.slice(0, -1).join(", ")} and ${parts[parts.length - 1]}`;
}

/**
 * The agency's health in the strip's footer row (finance-real round 4): whether the running work pays for running BeNerdy
 * each month, then what is out for clients and what is overdue. Profit is a net figure, red below zero (DD-054).
 */
function HealthLine({ health }: { health: CashHealth }) {
  const money = (minor: number) => formatMoney(minor, "EUR");
  return (
    <div className="flex flex-wrap items-baseline gap-x-6 gap-y-1 border-t border-gray-100 pt-2 text-xs text-gray-500">
      <span>
        {"Profit each month "}
        <MoneyText amountMinor={health.profitMonthlyMinor} balance strong className="text-sm" />
        <span className="text-gray-400">
          {` · margin ${money(health.marginMonthlyMinor)} before write-offs, less ${lessText(health, money)}`}
        </span>
      </span>
      <span>
        {`${FRONTED} `}
        <MoneyText amountMinor={health.frontedMinor} className="text-sm" />
      </span>
      <span>
        {health.overdueMinor > 0 ? (
          <>
            {"Overdue "}
            <MoneyText amountMinor={health.overdueMinor} className="text-sm" />
          </>
        ) : (
          "Nothing overdue"
        )}
      </span>
    </div>
  );
}

/**
 * The strip (DD-054): where we stand on the range's first day, today and its last, each under its date. Without the
 * agency's cash (A4) there is no level, so only the two ends show, each as how far it stands above or below today in
 * words (`fromToday`), never signed: a flow, never red.
 */
export function standingStats(view: AgencyCashView): Stat[] {
  const { start, today, end } = view.standing;
  const cells: [string, LineDay][] = [
    [`${view.days} days ago`, start],
    ["Today", today],
    [`In ${view.days} days`, end],
  ];
  if (view.position.positionMinor === null) {
    return cells
      .filter(([, day]) => day !== today)
      .map(([label, day]) => ({
        label,
        // MoneyText's cell in words, zero gray.
        value: <span className={`tabular-nums whitespace-nowrap font-semibold ${day.changeMinor === 0 ? "text-gray-400" : "text-gray-900"}`}>{fromToday(day.changeMinor)}</span>,
        sub: dateLabel(day.date),
      }));
  }
  return cells.map(([label, day]) => ({
    label,
    value: <MoneyText amountMinor={day.positionMinor} strong balance />,
    sub: day === today ? "where we stand" : dateLabel(day.date),
  }));
}

export function CashTab({ days, onOpen }: { days: CashDays; onOpen: (patch: ParamPatch) => void }) {
  const query = useCashTab(days);
  const data = query.data;
  if (!data) {
    return (
      <div className="flex-1 min-h-0 overflow-auto px-4 lg:px-6 pb-6 space-y-4">
        <StatStripPending failed={query.isError} />
        <TabPending failed={query.isError} message="Could not load the cash." retrying={query.isFetching} onRetry={() => void query.refetch()} />
      </div>
    );
  }
  return (
    <div className="flex-1 min-h-0 overflow-auto px-4 lg:px-6 pb-6 space-y-4">
      <section aria-labelledby="cash-where-we-stand" className="bg-white rounded-xl px-4 py-3">
        <h2 id="cash-where-we-stand" className="mb-2 text-sm font-semibold text-gray-900">
          Where we stand
        </h2>
        <StatStrip steady stats={standingStats(data)} footer={<HealthLine health={data.health} />} />
        {data.position.positionMinor === null && <BooksNote />}
        <div className="mt-3">
          <CashChart line={data.line} today={data.today} />
        </div>
      </section>
      <TodayMadeOf position={data.position} agencyCash={data.agencyCash} />
      <PositionPanels position={data.position} onOpen={onOpen} />
    </div>
  );
}
